import { Trash01, UploadCloud02 } from "@untitled-ui/icons-react";
import {
  type ReactNode,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import FileTypeIcon from "@/components/icons/FileTypeIcon";
import Loading from "@/components/icons/Loading";
import { cn } from "@/lib/utils";
import { convertBytesToReadable } from "@/utils/convertBytesToReadable";
import { notify } from "@/utils/notify";

import Button from "../forms/Button";
import Text from "../text/Text";
import Tooltip from "./Tooltip";

type DropZoneProps = {
  accept?: Array<"image/*" | "video/*" | "audio/*" | "application/*">;
  label?: ReactNode;
  preview?: string;
  previewFileName?: string;
  previewSize?: number;
  previewExtension?: string;
  progress?: number;
  children?: ReactNode;
  onUpload?: (file: File) => void;
  onDeleted?: () => void;
  hasBorder?: boolean;
  error?: boolean;
  maxFileSize?: number;
};

const mimeTypes = {
  "image/*": [".jpg", ".jpeg", ".png"],
  "video/*": [".mp4"],
  "audio/*": [".mp3", ".wav", ".ogg", ".m4a"],
  "application/*": [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".zip", ".rar"],
};

const imageExtensions = mimeTypes["image/*"].map((extension) =>
  extension.replace(".", ""),
);

export type DropZoneRef = {
  reset: () => void;
};

const DropZone = forwardRef<DropZoneRef, DropZoneProps>(function DropZone(
  {
    label,
    accept,
    preview,
    previewFileName,
    previewSize,
    previewExtension,
    progress,
    error,
    children,
    onUpload,
    onDeleted,
    hasBorder = true,
    maxFileSize,
  },
  ref,
) {
  const { t } = useTranslation("core.shared");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    accept: accept?.reduce(
      (acc, type) => ({ ...acc, [type]: mimeTypes[type] }),
      {},
    ),
    onDropAccepted: (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      if (!file) return;

      const extension = (file.name || "").split(".").pop()?.toLowerCase() || "";
      const mime = file.type;

      const isImageFile =
        mime.startsWith("image/") ||
        ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);

      const isExcelOrWord = ["xls", "xlsx", "doc", "docx"].includes(extension);

      let maxSize = 50 * 1024 * 1024;

      if (isImageFile) {
        maxSize = 5 * 1024 * 1024;
      } else if (isExcelOrWord) {
        maxSize = 10 * 1024 * 1024;
      }

      if (maxFileSize != null) {
        maxSize = maxFileSize;
      }

      if (file.size > maxSize) {
        notify.error(t("The selected file is larger than the allowed size"));
        return;
      }

      setSelectedFile(file);

      onUpload?.(file);
    },
  });

  useImperativeHandle(ref, () => {
    return {
      reset: () => {
        setSelectedFile(null);
        if (inputRef.current) inputRef.current.value = "";
      },
    };
  }, [inputRef]);

  const hasFile = selectedFile || preview;
  const image = selectedFile
    ? URL.createObjectURL(selectedFile)
    : preview
      ? preview
      : null;

  const extension = selectedFile
    ? (selectedFile.name || "").split(".").pop()
    : previewExtension;

  const isImagePreview = extension
    ? imageExtensions.includes(extension.toLowerCase())
    : false;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (inputRef.current) inputRef.current.value = "";
    onDeleted?.();
  };

  return (
    <>
      <div
        {...getRootProps({})}
        className={cn(
          "rounded-xl text-sm",
          hasBorder && "border",
          isDragActive && "border-primary-600 ring-primary-600 ring-1",
          error && "border-error-600",
        )}
      >
        <input {...getInputProps()} />
        {children ? (
          children
        ) : hasFile ? (
          <div className="flex items-center gap-3 p-4">
            {isImagePreview && image ? (
              <div className="relative size-10 shrink-0">
                <div className="size-10 overflow-hidden rounded-full">
                  <img
                    src={image}
                    alt={selectedFile?.name || previewFileName || t("Preview")}
                    className="size-full object-cover"
                  />
                </div>
              </div>
            ) : (
              <FileTypeIcon extension={extension} />
            )}
            <div className="min-w-0 flex-1 text-sm">
              <Tooltip content={selectedFile?.name || previewFileName}>
                <div className="truncate">
                  {selectedFile?.name || previewFileName}
                </div>
              </Tooltip>
              <Text color="gray" className="flex gap-1">
                {convertBytesToReadable(previewSize || selectedFile?.size || 0)}
                {progress !== undefined && (
                  <>
                    <span>-</span>
                    <span>{progress + "%"}</span>
                  </>
                )}
              </Text>
            </div>
            {progress !== undefined ? (
              <Loading className="size-8 self-start" />
            ) : (
              <Button
                color="link-color"
                variant="error"
                className="self-start"
                onClick={handleDelete}
              >
                <Trash01 className="size-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 px-6 py-4">
            <span className="flex size-10 items-center justify-center rounded-full bg-gray-50">
              <span className="rounded-full bg-gray-100 p-1">
                <UploadCloud02 className="size-5 text-gray-600" />
              </span>
            </span>
            <div className="min-w-0 flex-1">
              {label || (
                <div className="flex flex-col gap-1 text-center">
                  <div>
                    <span className="text-primary-700 text-sm font-semibold">
                      {t("Upload")}{" "}
                    </span>
                    <span>{t("or drop your file here")}</span>
                  </div>
                  <span className="text-xs font-normal text-gray-600">
                    SVG, PNG, JPG or GIF (max. 800x400px)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default DropZone;
